import type { InfiniteData } from '@tanstack/react-query'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import { cloneDeep, debounce, uniq } from 'lodash'
import { v4, validate } from 'uuid'
import { queryClient } from 'v2/configs/rq-setup'
import { conversationQueryKeys, getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { isRedacted } from 'v2/shared/utils/redaction'
import { useSharedConversationStore } from 'v2/state/conversation'
import { UserRole } from 'v2/types/api/auth'
import {
  AdhocChannels,
  ConvChannels,
  ConversationStatus,
  ConvGeneratedTypes,
  type RActiveConversation,
  type RChunk,
  type RNewConversation,
  type RTask,
} from 'v2/types/api/conversations'
import type { RRoutingSettings, RUserProfile } from 'v2/types/api/settings'
import { CallDirection, type CallMeta, MonitorStates } from 'v2/types/call'
import { type Chunk, ChunkSender, ChunkType } from 'v2/types/chunk.internal'
import { getLocalAuth, sessionHasRole } from 'v2/utils'
import { logger } from 'v2/utils/logger'
import { TaskStatus } from '../tasks/tasks.config'
import { isActiveConversation, isNewConversation } from './typeguards'

export const formatPhoneNumber = (text?: string, fallback = ''): string => {
  if (!text) return fallback
  // A redacted number never parses, so callers that pass a fallback would render it as "-"
  // (or as the raw pre-redaction value) instead of showing that it is deliberately hidden.
  if (isRedacted(text)) return text
  const formattedText = `+${text.replace(/^\+/, '')}`
  return isValidPhoneNumber(formattedText)
    ? parsePhoneNumber(formattedText)?.formatInternational() || fallback
    : fallback || text
}

export function truncateFileName(fileName: string, maxLength?: number): string {
  if (!maxLength || !fileName) return fileName
  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex === -1) return fileName.slice(0, maxLength)

  const name = fileName.slice(0, lastDotIndex)
  const extension = fileName.slice(lastDotIndex)

  if (name.length <= maxLength) return fileName

  return `${name.slice(0, maxLength)}..${extension}`
}

export const replaceUuidWithText = (text?: string, replaceWith?: string): string => {
  if (!text) return ''
  if (validate(text)) return replaceWith || ''
  return text
}

export const getFormattedContactName = (contactName?: string): string => {
  if (!contactName) return ''
  return formatPhoneNumber(contactName, contactName)
}

export const getFormattedContactCountry = (contactName: string): string => {
  if (!contactName) return ''
  const text = `+${contactName.replace(/^\+/, '')}`
  return isValidPhoneNumber(text) ? parsePhoneNumber(text)?.country || '' : ''
}

export const getAgentSip = () => {
  const profile = queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)
  if (
    !profile ||
    !profile.organizations ||
    !profile.organizations[0] ||
    !profile.organizations[0].sip_endpoint
  )
    return ''
  return `sip:${profile.organizations[0].sip_endpoint.username}@phone.plivo.com`
}

export const getAgentTimezone = () => {
  const profile = queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)
  if (
    !profile ||
    !profile.organizations ||
    !profile.organizations[0] ||
    !profile.organizations[0].timezone
  )
    return 'UTC'
  return profile.organizations[0].timezone
}

export const getOutgoingCallDirection = (meta?: CallMeta, direction?: CallDirection) =>
  meta === 'Outgoing call transfer' ? CallDirection.OUTBOUND : direction || CallDirection.INBOUND

export const parseConversationURL = () => {
  const { pathname } = window.location
  const pathnameRegex = /^\/conversations\/([a-z0-9-]+)(\/c\/)?(\d+)?/g
  const pathParts = pathnameRegex.exec(pathname)
  // 2nd group is the conversation number
  const conversationNumber = pathParts?.[3]
  const viewId = pathParts?.[1]
  return { conversationNumber, viewId }
}

export const getConversationURL = (conversationNumber: string, viewId?: string) => {
  const { viewId: viewIdFromURL } = parseConversationURL()
  if (!conversationNumber) return `/conversations/${viewId || viewIdFromURL}`
  return `/conversations/${viewId || viewIdFromURL}/c/${conversationNumber}`
}

export const isDetailView = () => {
  const { pathname } = window.location
  return pathname.includes('/c/') || pathname.endsWith('/c')
}

export const getProperTimetoken = (timetoken?: string | number | null): number => {
  const PUBNUB_TIMETOKEN_THRESHOLD = 1000000000000000
  const numericTimetoken = Number(timetoken)

  if (Number.isNaN(numericTimetoken)) return 0

  return numericTimetoken >= PUBNUB_TIMETOKEN_THRESHOLD
    ? Math.floor(numericTimetoken / 10000)
    : numericTimetoken
}

export const getPubnubTimetoken = (timetoken: string | number): number => {
  try {
    const tt = new Date(getProperTimetoken(timetoken)).getTime()
    if (Number.isNaN(tt)) return 0
    return tt * 10000
  } catch (error) {
    logger.error('Error getting pubnub timetoken', error)
    return 0
  }
}

export const getUnknownChunk = (m: RChunk): Chunk<ChunkType.unknown> => {
  logger.warn('Unknown chunk type', m)
  return {
    id: v4(),
    sender: ChunkSender.UNKNOWN,
    meta: { data: m } as never,
    channel: 'unknown' as ConvChannels,
    timetoken: new Date(m.timestamp).getTime(),
    type: ChunkType.unknown,
  }
}

export const calculateUnreadCounts = (conversationNumber: string, chunks: Chunk[]): number => {
  const lastReadTimestamp =
    useSharedConversationStore.getState().lastReadTimestamp[conversationNumber]
  const unreadChunksCount = chunks.filter(
    (chunk) => getProperTimetoken(chunk.timetoken) > lastReadTimestamp,
  ).length

  return unreadChunksCount
}

export const debouncedMicotask = debounce((fn) => {
  queueMicrotask(fn)
  return Promise.resolve()
}, 50)

// Feature: Unread count management
export const updateUnreadCounts = (conversationNumber: string, chunks: Chunk[]): void => {
  queueMicrotask(() => {
    const unreadChunksCount = calculateUnreadCounts(conversationNumber, chunks)
    useSharedConversationStore.getState().setUnreadCounts(conversationNumber, unreadChunksCount)
    logger.debug(`Unread chunks for conversation ${conversationNumber}: ${unreadChunksCount}`)
  })
}

export const updateUnreadCountsForConversation = async (
  conversationNumber: string,
): Promise<void> => {
  const data = queryClient.getQueryData<InfiniteData<Chunk>>(
    conversationQueryKeys().chunks(conversationNumber),
  )
  const chunks = data?.pages.flat() || []
  await updateUnreadCounts(conversationNumber, chunks)
}

export const checkAsyncWarmTransferInProgress = (
  conversation: RActiveConversation | RNewConversation,
) =>
  isActiveConversation(conversation) &&
  conversation.child_conversations.some((c) => c.state === ConversationStatus.TRANSFERRING) &&
  conversation.secondary_participants_details.length > 0

export const checkSecondaryParticipant = (conversation?: RActiveConversation | RNewConversation) =>
  isActiveConversation(conversation) &&
  conversation.secondary_participants_details.length > 0 &&
  conversation.secondary_participants.includes(getLocalAuth().aom_uuid)

/*
  If the active conversation has any child conversation with a generation type of QNR_GENERATED, TRANSFER_QNR_GENERATED, TRANSFER_GENERATED, or OUTBOUND_GENERATED
  then it belongs to owner of the parent conversation who shall hold the ability to resolve and transfer conversation.
  All the other agents who are part of the same conversation due to tasks can only view the conversation and cannot resolve or transfer it but can perform outbound calls.
*/
export const isTaskConversation = (conversations: RActiveConversation['child_conversations']) =>
  !(
    conversations?.length &&
    conversations.some((c) =>
      [
        ConvGeneratedTypes.QNR_GENERATED,
        ConvGeneratedTypes.TRANSFER_QNR_GENERATED,
        ConvGeneratedTypes.TRANSFER_GENERATED,
        ConvGeneratedTypes.OUTBOUND_GENERATED,
        ConvGeneratedTypes.CONTACT_ID_SWITCH,
        ConvGeneratedTypes.CHANNEL_SWITCH,
      ].includes(c.generation_type as ConvGeneratedTypes),
    )
  )

export const checkAgentHasOpenTasks = (tasks: RTask[], agentId: string) => {
  const agentHasOpenTasks = tasks.some(
    (task) => task.destination_id === agentId && task.status !== TaskStatus.resolved,
  )
  return Boolean(agentHasOpenTasks)
}

// Canonical display names — raw channel values must never reach the UI
// (CSS-capitalized "Whatsapp"/"Message" read as brand/naming drift).
const CHANNEL_LABELS: Record<string, string> = {
  call: 'Call',
  chat: 'Chat',
  notes: 'Notes',
  whatsapp: 'WhatsApp',
  message: 'SMS',
  omni: 'Omni',
  slack: 'Slack',
}

export const getChannelLabel = (channel?: string): string => {
  if (!channel) return ''
  const key = channel.toLowerCase()
  return CHANNEL_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1)
}

// CONTACT_RELATED
export const getAvailableChannels = (conversation?: RActiveConversation | RNewConversation) => {
  // Return empty array if no conversation exists
  if (!conversation) return []

  // For new conversations, only return the initial channel
  if (isNewConversation(conversation)) {
    if (conversation.channel === AdhocChannels.TASK) {
      return []
    }
    return [conversation.channel]
  }

  // For task conversations, no channels should be available
  if (isTaskConversation(conversation.child_conversations)) {
    return []
  }

  // For non-phone number contacts (e.g., email, social)
  if (!isValidPhoneNumber(conversation.contact_identifier)) {
    // Only return chat if there's an existing chat conversation
    if (conversation.child_conversations.some((c) => c.channel === ConvChannels.CHAT))
      return [ConvChannels.CHAT]
    return []
  }

  // During async warm transfer, secondary participants can only see notes
  if (checkSecondaryParticipant(conversation)) {
    return []
  }

  // For all other cases, return unique channels:
  return uniq(
    conversation.child_conversations
      .map((c) => c.channel)
      // Add default messaging channels for phone number contacts
      .concat([ConvChannels.CHAT, ConvChannels.MESSAGE, ConvChannels.WHATSAPP] as ConvChannels[])
      // Remove task, callback, and call channels
      .filter(
        (c) => c !== AdhocChannels.TASK && c !== AdhocChannels.CALLBACK && c !== ConvChannels.CALL,
      )
      // Only include chat if there's an open chat conversation
      .filter((c) => {
        if (c === ConvChannels.CHAT)
          return conversation.child_conversations.some(
            (cc) => cc.channel === ConvChannels.CHAT && !cc.end_at,
          )
        return true
      }),
  )
}

export const getCallConversation = (conversations: RActiveConversation[]) => {
  const childFilter = (c: RActiveConversation['child_conversations'][number]) =>
    c.channel === ConvChannels.CALL && !c.end_at && !c.wrap_up_start_at

  const parentConversation = conversations.find((c) => c.child_conversations.some(childFilter))
  const callChildConversation = parentConversation?.child_conversations.find(childFilter)
  if (!parentConversation || !callChildConversation) return {}
  return { parentConversation, callChildConversation }
}

const filterTaskAndCallbackConversations = (
  conversations: RActiveConversation['child_conversations'],
) =>
  conversations.filter(
    (c) => c.channel !== AdhocChannels.TASK && c.channel !== AdhocChannels.CALLBACK,
  )

export const getFirstChildConversation = (
  conversation: RActiveConversation | RNewConversation,
): RActiveConversation['child_conversations'][0] | null => {
  if (isNewConversation(conversation)) {
    return null
  }
  const filteredConversations = filterTaskAndCallbackConversations(conversation.child_conversations)
  if (filteredConversations.length === 0) return null
  return filteredConversations[0]
}

export const getLastChildConversation = (
  conversation?: RActiveConversation | RNewConversation,
): RActiveConversation['child_conversations'][number] | null => {
  if (!conversation) return null
  if (isNewConversation(conversation)) {
    return null
  }
  const filteredConversations = filterTaskAndCallbackConversations(conversation.child_conversations)
  if (filteredConversations.length === 0) return null
  return filteredConversations[filteredConversations.length - 1]
}

export const getConversationChannel = (conversation: RActiveConversation | RNewConversation) => {
  if (isNewConversation(conversation)) {
    if (conversation.channel === AdhocChannels.TASK) {
      return AdhocChannels.NOTES
    }
    return conversation.channel
  }
  if (!isActiveConversation(conversation)) return undefined
  if (checkSecondaryParticipant(conversation) && checkAsyncWarmTransferInProgress(conversation)) {
    return AdhocChannels.NOTES
  }

  // Return notes by default if there is an open call
  if (conversation.child_conversations.some((c) => c.channel === ConvChannels.CALL && !c.end_at))
    return ConvChannels.NOTES

  // Return notes by default if there is no available channels
  if (getAvailableChannels(conversation).length === 0) return AdhocChannels.NOTES

  return getLastChildConversation(conversation)?.channel || AdhocChannels.NOTES
}

export const checkIsTransferring = (conversation?: RActiveConversation | RNewConversation) =>
  conversation &&
  !isNewConversation(conversation) &&
  conversation?.child_conversations.some((c) => c.state === ConversationStatus.TRANSFERRING)

export const getWrapupChildConversation = (conversation?: RActiveConversation | RNewConversation) =>
  isActiveConversation(conversation) &&
  conversation.child_conversations.every((c) => c.status === ConversationStatus.IN_WRAP_UP) &&
  conversation.child_conversations.findLast((c) => c.status === ConversationStatus.IN_WRAP_UP)

// Tanstack doesnt accept dot notation this util function converts to tanstack and BE Friendsly format
export const formatVisibileColumnName = (columnId: string): string =>
  columnId
    .replace('agent_details_agent_name', 'agent_details.agent_name')
    .replace('agent_details_teams_team_name', 'agent_details.teams.team_name')

export const reverseFormatVisibleColumnName = (columnId: string): string =>
  columnId
    .replace('agent_details.agent_name', 'agent_details_agent_name')
    .replace('agent_details.teams.team_name', 'agent_details_teams_team_name')

export const isInitiatingConversation = (conversation: RNewConversation) =>
  conversation.invite_type === 'INITIATE'

interface CallValidationResult {
  shouldDisable: boolean
  disabledReason?: string
}

export function validateCallAction(params: {
  activeCall?: boolean
  isPhoneConnected?: boolean
  selectedSender?: { name: string; phoneNumber: string }
  activeConversations?: RActiveConversation[]
}): CallValidationResult {
  const { activeCall, isPhoneConnected, selectedSender, activeConversations } = params
  const routingSettings = queryClient.getQueryData<RRoutingSettings>(
    getSettingsQueryKeys().routingSettings,
  )
  const hasOpenCallChildConversation = activeConversations?.some((c) =>
    c.child_conversations.some((cc) => cc.channel === ConvChannels.CALL && !cc.end_at),
  )

  if (activeCall) return { shouldDisable: true, disabledReason: 'Call in progress' }
  if (!isPhoneConnected)
    return { shouldDisable: true, disabledReason: 'Phone service not connected' }
  if (!selectedSender) return { shouldDisable: true, disabledReason: 'Please select a caller ID' }

  // Check routing settings and active call conversation
  if (hasOpenCallChildConversation) {
    if (!routingSettings?.release_capacity_on_resolve) {
      return {
        shouldDisable: true,
        disabledReason: 'Please wrap up the active call conversation before initiating a new one',
      }
    }
  }

  return { shouldDisable: false }
}

// Monitoring
export const isMonitoring = (state?: MonitorStates | null) =>
  state === MonitorStates.MONITOR ||
  state === MonitorStates.MONITOR_VISIBLE ||
  state === MonitorStates.ASSISTANCE_REQUESTED

export const isOneOfMonitoringStates = (state?: MonitorStates | null) =>
  isMonitoring(state) || state === MonitorStates.COACH || state === MonitorStates.BARGE

export const isSupervisor = () => sessionHasRole(UserRole.supervisor)

export const isAgent = () => sessionHasRole(UserRole.agent)

export const isAdministrator = () => sessionHasRole(UserRole.administrator)

// View table utilities

/**
 * Transforms view filters by extracting UUIDs from object arrays.
 * Filter values that contain objects with `uuid` property are converted to arrays of UUIDs.
 */
export function transformViewFilters<
  T extends { filter_groups?: unknown[] } | Record<string, never> | null | undefined,
>(filters: T): T extends { filter_groups: unknown[] } ? T : Record<string, never> {
  if (!filters || !('filter_groups' in filters) || !filters.filter_groups) {
    return {} as T extends { filter_groups: unknown[] } ? T : Record<string, never>
  }

  const transformed = cloneDeep(filters) as T & {
    filter_groups: Array<{ filter_children: Array<{ value: unknown }> }>
  }
  transformed.filter_groups = transformed.filter_groups.map((group) => ({
    ...group,
    filter_children: group.filter_children.map((child) => {
      const hasUuidObjects =
        Array.isArray(child.value) &&
        typeof child.value[0] === 'object' &&
        child.value[0] !== null &&
        'uuid' in child.value[0]

      if (hasUuidObjects) {
        return {
          ...child,
          value: (child.value as Array<{ uuid: string }>).map((item) => item.uuid),
        }
      }
      return child
    }),
  }))

  return transformed as T extends { filter_groups: unknown[] } ? T : Record<string, never>
}

// Default fields for view records table
export const VIEW_TABLE_FIELDS = [
  'conv_number',
  'conv_child_sender_ids',
  'customer_name',
  'contact_identifier',
  'csat_feedback',
  'agent_details.agent_name',
  'agent_details.teams.team_name',
  'parent_channel',
  'direction_inbound',
  'conv_created_date',
  'conv_created_time',
  'disposition',
  'wrap_up_note',
  'case_id',
  'hangup_source',
] as const
